import React, { useCallback } from "react";
import { CircularProgress } from "@mui/material";
import {
    SovosBox,
    SovosButton,
    SovosIconButton,
    SovosTypography,
} from "mosaic-react";
import { AutoAwesome, Close } from "mosaic-react-icons";
import { DataGrid, rows } from "colbi_web_ui/lib/components";
import { KPI_BORDER_COLORS } from "../constants";
import { stripCdmPrefix } from "../utils";
import MappingDropdown from "../components/MappingDropdown";
import MappingGridToolbar from "../components/MappingGridToolbar";
import StatTile from "../components/StatTile";

const StepMapColumns = ({
    needsMappingCount,
    readyCount,
    skippedCount,
    totalColumns,
    mappings,
    mschFields,
    usedFields,
    aiSuggestedColumns,
    aiPendingSuggestions,
    aiAppliedSkippedCsv,
    originalMappingsRef,
    hideDoNotImport,
    showDoNotImportToggle,
    dgItems,
    dgHeader,
    dgAvailableFilters,
    i18n,
    entityLabel,
    onChangeEntity,
    loading,
    onMappingChange,
    onRevert,
    onDismissAiBanner,
    onAcceptAiRow,
    onRejectAiRow,
    onToggleHideDoNotImport,
}) => {
    const columnHeaderRenderer = useCallback(
        (headerItem) => {
            const csvColumn = headerItem.columnName;
            const displayName = headerItem.headerName || csvColumn;
            const value = mappings[csvColumn] ?? null;
            const isAiSuggested = aiSuggestedColumns.has(csvColumn);

            return (
                <SovosBox sx={{ display: "flex", flexDirection: "column", width: "100%", py: 0.5, gap: 0.5 }}>
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <SovosTypography variant="caption" fontWeight={600} color="text.primary" noWrap>
                            {displayName}
                        </SovosTypography>
                        {isAiSuggested && (
                            <AutoAwesome
                                sx={{ fontSize: 11, color: "primary.main" }}
                                titleAccess={i18n`Applied by AI`}
                            />
                        )}
                    </SovosBox>
                    <MappingDropdown
                        csvColumn={csvColumn}
                        displayName={displayName}
                        value={value}
                        originalValue={originalMappingsRef.current[csvColumn] ?? null}
                        mschFields={mschFields}
                        usedFields={usedFields}
                        aiScore={isAiSuggested ? aiSuggestedColumns.get(csvColumn) : null}
                        onChange={onMappingChange}
                        onRevert={onRevert}
                        entityLabel={entityLabel}
                        onChangeEntity={onChangeEntity}
                        i18n={i18n}
                    />
                </SovosBox>
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mappings, usedFields, mschFields, aiSuggestedColumns, onMappingChange, onRevert]
    );

    if (loading) {
        return (
            <SovosBox
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    minHeight: "calc(100vh - 280px)",
                }}
            >
                <CircularProgress size={28} />
                <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                    {i18n`Loading mapping data…`}
                </SovosTypography>
            </SovosBox>
        );
    }

    return (
        <>
            <SovosBox sx={{ display: "flex", gap: 1.5, mb: aiSuggestedColumns.size > 0 || aiPendingSuggestions.size > 0 ? 1.5 : 3 }}>
                <StatTile label={i18n`Columns need to be mapped`} value={needsMappingCount} borderColor={KPI_BORDER_COLORS[0]} />
                <StatTile label={i18n`Ready for next step`} value={readyCount} borderColor={KPI_BORDER_COLORS[1]} />
                <StatTile label={i18n`Columns skipped mapping`} value={skippedCount} borderColor={KPI_BORDER_COLORS[2]} />
                <StatTile label={i18n`All columns`} value={totalColumns} borderColor={KPI_BORDER_COLORS[3]} />
            </SovosBox>

            {entityLabel && (
                <SovosTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {i18n`You are mapping imported data to SAF-T ${entityLabel} data`}
                </SovosTypography>
            )}

            {(aiSuggestedColumns.size > 0 || aiPendingSuggestions.size > 0) && (
                <SovosBox sx={{ mb: 3, border: "1px solid", borderColor: "warning.light", borderRadius: 1, overflow: "hidden" }}>
                    <SovosBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            bgcolor: "warning.light",
                            borderBottom: "1px solid",
                            borderColor: "warning.light",
                        }}
                    >
                        <AutoAwesome sx={{ fontSize: 14, color: "warning.dark" }} />
                        <SovosTypography variant="caption" fontWeight={600} color="warning.dark" sx={{ flex: 1 }}>
                            {i18n`${aiPendingSuggestions.size} suggestions pending review.`}{" "}
                            {aiSuggestedColumns.size > 0
                                ? i18n`${aiSuggestedColumns.size} suggestions already accepted.`
                                : i18n`Accept or reject each suggestion individually.`}
                        </SovosTypography>
                        <SovosIconButton size="small" tooltipText={i18n`Dismiss`} onClick={onDismissAiBanner}>
                            <Close sx={{ fontSize: 14 }} />
                        </SovosIconButton>
                    </SovosBox>
                    {aiAppliedSkippedCsv > 0 && (
                        <SovosBox sx={{ px: 1.5, py: 0.75, bgcolor: "warning.light", borderBottom: "1px solid", borderColor: "warning.light" }}>
                            <SovosTypography variant="caption" color="warning.dark">
                                {i18n`${aiAppliedSkippedCsv} proposed rows were not applied because their CSV column names did not match this file.`}
                            </SovosTypography>
                        </SovosBox>
                    )}
                    <SovosBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 1.5,
                            py: 0.5,
                            bgcolor: "action.selected",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ width: 140, flexShrink: 0 }}>{i18n`CSV column`}</SovosTypography>
                        <SovosTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>→</SovosTypography>
                        <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1 }}>{i18n`Map to`}</SovosTypography>
                        <SovosTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ width: 100, flexShrink: 0, textAlign: "right" }}
                            title={i18n`How strong this proposed link is (0–100%)`}
                        >
                            {i18n`Confidence`}
                        </SovosTypography>
                    </SovosBox>
                    <SovosBox sx={{ maxHeight: 260, overflowY: "auto" }}>
                        {[...aiPendingSuggestions.entries()].map(([col, suggestion], idx) => {
                            const targetField = suggestion.gridId;
                            const field = mschFields.find((f) => f.id === targetField);
                            return (
                                <SovosBox
                                    key={col}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        px: 1.5,
                                        py: 0.75,
                                        bgcolor: idx % 2 === 0 ? "action.hover" : "transparent",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <SovosTypography variant="caption" fontWeight={600} sx={{ width: 140, flexShrink: 0 }}>
                                        {stripCdmPrefix(col)}
                                    </SovosTypography>
                                    <SovosTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>→</SovosTypography>
                                    <SovosTypography variant="caption" color="primary.main" sx={{ flex: 1 }}>
                                        {field?.name || stripCdmPrefix(targetField) || "—"}
                                    </SovosTypography>
                                    <SovosBox
                                        sx={{ px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor: "success.light", flexShrink: 0, width: 100, display: "flex", justifyContent: "flex-end" }}
                                        title={i18n`How strong this proposed link is (0–100%)`}
                                    >
                                        <SovosTypography sx={{ fontSize: 10, color: "success.dark", fontWeight: 600 }}>
                                            {Math.round((suggestion.score ?? 0) * 100)}%
                                        </SovosTypography>
                                    </SovosBox>
                                </SovosBox>
                            );
                        })}
                        {[...aiPendingSuggestions.entries()].map(([col, suggestion], idx) => (
                            <SovosBox
                                key={`${col}-actions-${idx}`}
                                sx={{
                                    px: 1.5,
                                    py: 0.75,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 1,
                                    bgcolor: idx % 2 === 0 ? "action.hover" : "transparent",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <SovosTypography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
                                    {suggestion.rationale || i18n`AI matched this column by semantic similarity.`}
                                </SovosTypography>
                                <SovosBox sx={{ display: "flex", gap: 0.5 }}>
                                    <SovosButton variant="text" size="small" onClick={() => onRejectAiRow(col)}>
                                        {i18n`Reject`}
                                    </SovosButton>
                                    <SovosButton variant="contained" size="small" onClick={() => onAcceptAiRow(col)}>
                                        {i18n`Accept`}
                                    </SovosButton>
                                </SovosBox>
                            </SovosBox>
                        ))}
                    </SovosBox>
                </SovosBox>
            )}

            <DataGrid
                key={`column-mapping-grid-${hideDoNotImport}`}
                id="column-mapping-grid"
                items={dgItems}
                header={dgHeader}
                card={rows.CellCard}
                status={{ loading: false }}
                metadata={{ total: dgItems.length }}
                enablePagination
                headerHeight={80}
                columnHeaderRenderer={columnHeaderRenderer}
                availableFilters={dgAvailableFilters}
                toolbar={MappingGridToolbar}
                toolbarProps={{ hideDoNotImport, onToggleHideDoNotImport, showDoNotImportToggle }}
                showCheckbox={false}
                modifierssx={{
                    "& .MuiDataGrid-columnHeader": { alignItems: "flex-start", pt: 1 },
                    "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal" },
                }}
            />

        </>
    );
};

export default StepMapColumns;
